import { Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { STATUS } from "../../constants/const";
import { loginWithFirebase, verifySession } from "../../api/auth";
import UserAuthStore from "../../store/userStore";
import authService from "../../services/AuthService";
import useFeedbackStore from "../../store/useFeedbackStore";
import Loader from "../../components/Loader/Loader";
import logger from "../../services/Logger";
import {
  getSessionToken,
  invalidateAuthSession,
  persistAuthSession,
  subscribeToAuthSessionChanges,
} from "../../services/AuthSession";

let pendingSessionVerification = null;
let pendingSessionToken = null;

function verifySessionOnce(sessionToken) {
  if (!pendingSessionVerification || pendingSessionToken !== sessionToken) {
    pendingSessionToken = sessionToken;
    const verification = verifySession(sessionToken);
    pendingSessionVerification = verification;
    verification.then(
      () => {
        if (pendingSessionVerification === verification) {
          pendingSessionVerification = null;
          pendingSessionToken = null;
        }
      },
      () => {
        if (pendingSessionVerification === verification) {
          pendingSessionVerification = null;
          pendingSessionToken = null;
        }
      }
    );
  }

  return pendingSessionVerification;
}

function CheckLogin() {
  const [status, setStatus] = useState(STATUS.LOADING);
  const [sessionRevision, setSessionRevision] = useState(0);
  const setAuthenticated = UserAuthStore((state) => state.setAuthenticated);
  const setAuthStatus = UserAuthStore((state) => state.setAuthStatus);

  useEffect(() => {
    let isMounted = true;
    const unsubscribe = subscribeToAuthSessionChanges((event) => {
      if (!isMounted) return;
      if (event?.type === "invalidated") {
        Promise.resolve(authService.signOut?.()).catch(() => {});
        UserAuthStore.getState().clearAuthSession();
        setStatus(STATUS.ERROR);
        return;
      }
      setSessionRevision((revision) => revision + 1);
    });
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let unsubscribeAuth;

    const getFirebaseUser = () => new Promise((resolve) => {
      unsubscribeAuth = authService.onAuthStateChanged(resolve);
    });

    const verifyAuth = async () => {
      const sessionToken = getSessionToken();
      let activeSessionToken = sessionToken;
      try {
        setStatus(STATUS.LOADING);
        setAuthStatus("resolving");
        const currentUser = await getFirebaseUser();
        unsubscribeAuth?.();
        if (!isMounted) {
          return;
        }

        if (!currentUser) {
          invalidateAuthSession({
            reason: "firebase_user_missing",
            source: "check_login",
            expectedToken: sessionToken,
            notifyCurrentTab: false,
          });
          setStatus(STATUS.ERROR);
          return;
        }

        let session;
        if (sessionToken) {
          try {
            session = await verifySessionOnce(sessionToken);
          } catch (error) {
            logger.warn('CheckLogin', 'Photon session verification rejected; renewing', {
              error: error.message,
            });
          }
        }

        if (!session) {
          const firebaseToken = await authService.getIdToken(currentUser, true);
          session = await loginWithFirebase(firebaseToken);
          if (getSessionToken() !== sessionToken) return;
          persistAuthSession({
            sessionToken: session.session_token,
            userId: session.user_id,
            orgId: session.org_id,
          }, "check_login_recovery");
          activeSessionToken = session.session_token;
        }

        if (!isMounted || getSessionToken() !== activeSessionToken) return;
        setAuthenticated(currentUser, session.org_id);
        setStatus(STATUS.SUCCESS);
      } catch (error) {
        if (!isMounted || getSessionToken() !== sessionToken) return;
        logger.error('CheckLogin', 'Session verification failed', { error: error.message });
        useFeedbackStore.getState().showFeedback("Session expired. Please login again.", "error");
        invalidateAuthSession({
          reason: "session_recovery_failed",
          source: "check_login",
          expectedToken: sessionToken,
          notifyCurrentTab: false,
        });
        setStatus(STATUS.ERROR);
      }
    };

    verifyAuth();

    return () => {
      isMounted = false;
      unsubscribeAuth?.();
    };
  }, [sessionRevision, setAuthenticated, setAuthStatus]);

  if (status === STATUS.LOADING)
    return (
      <div className="h-screen flex items-center justify-center bg-surface-page">
        <Loader />
      </div>
    );

  return <Outlet />;
}

export default CheckLogin;
